import React, { useState, useRef } from "react";
import styled from 'styled-components';
import useFetch from './hooks/useFetch';
import patternBackground from './images/prosolve-patterns-shapes-04.png';
import prosolveLogo from './images/prosolve-logo-dark-horizontal.png';
import {
  TextLink
} from './sharedComponents/AuthPageLayout';

// Student email/password login is descoped for now — to restore it, uncomment every
// block marked "EMAIL/PASSWORD LOGIN" below plus these imports (and re-add
// useEffect to the react import above):
// import { useEffect } from "react";
// import useForm from './hooks/useForm';
// import { FormField, LoadingIcon } from '@prosolve/common-components-ui'
// import {
//   FieldWrap,
//   fieldLabelStyle,
//   ErrorSlot,
//   SubmitRow,
//   SubmitButton,
//   SpinnerWrap,
//   Divider
// } from './sharedComponents/AuthPageLayout';

const CODE_LENGTH = 7;

const Page = styled.div`
  height: 100%;
  box-sizing: border-box;
  display: flex;
  padding: 24px;
  position: relative;
  overflow: auto;
  overflow-x: hidden;
  background: #001323 url(${patternBackground}) center center / cover no-repeat;
`;

const Veil = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: radial-gradient(ellipse 70% 70% at 50% 50%, rgba(0,19,35,.35) 0%, rgba(0,19,35,.72) 100%);
`;

const Card = styled.main`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 520px;
  margin: auto;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 28px;
  padding: 44px 44px 40px;
  box-shadow: 0 30px 70px -18px rgba(0,19,35,.55), 0 6px 20px rgba(0,19,35,.25);
  text-align: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #001323;
  @media (max-width: 520px) {
    padding: 36px 22px 30px;
    border-radius: 22px;
  }
`;

const Brand = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 22px;
`;

const Logo = styled.img`
  height: 38px;
  width: auto;
  display: block;
`;

const Title = styled.h1`
  font-family: 'Raleway', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -.01em;
  color: #001323;
  margin: 0 0 8px;
  @media (max-width: 520px) {
    font-size: 28px;
  }
`;

const Lead = styled.p`
  font-size: 17px;
  color: #4a6577;
  font-weight: 600;
  margin: 0 0 30px;
  @media (max-width: 520px) {
    font-size: 15px;
  }
`;

const CodeLabel = styled.div`
  font-family: 'Raleway', sans-serif;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .1em;
  color: #4a6577;
  margin-bottom: 14px;
`;

const CodeRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 8px;
  @media (max-width: 520px) {
    gap: 5px;
  }
`;

const CodeBox = styled.input`
  width: 50px;
  height: 62px;
  border: 2.5px solid ${props => props.$filled ? '#009db7' : '#d5dce5'};
  border-radius: 14px;
  background: ${props => props.$filled ? '#ffffff' : '#f8fafb'};
  font-family: 'Raleway', sans-serif;
  font-size: 28px;
  font-weight: 800;
  text-align: center;
  color: #001323;
  text-transform: uppercase;
  transition: border-color .15s, box-shadow .15s, background .15s, transform .1s;
  &:focus {
    outline: none;
    background: #ffffff;
    border-color: #30cfe5;
    box-shadow: 0 0 0 5px rgba(48,207,229,.22);
    transform: translateY(-2px);
  }
  @media (max-width: 520px) {
    width: 40px;
    height: 50px;
    font-size: 22px;
    border-radius: 11px;
  }
  @media (max-width: 360px) {
    width: 35px;
    height: 45px;
    font-size: 19px;
  }
`;

const Hint = styled.p`
  font-size: 13.5px;
  color: ${props => props.$error ? '#c8324f' : '#8897a8'};
  font-weight: ${props => props.$error ? 600 : 400};
  margin: 0 0 26px;
  min-height: 18px;
`;

const GoButton = styled.button`
  width: 100%;
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: .01em;
  color: #ffffff;
  background: #009db7;
  border: none;
  border-radius: 16px;
  padding: 17px;
  cursor: pointer;
  transition: background .15s, transform .05s, opacity .15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  &:hover {
    background: #007691;
  }
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: .5;
    cursor: not-allowed;
    background: #009db7;
    transform: none;
  }
  svg {
    transition: transform .15s;
  }
  &:not(:disabled):hover svg {
    transform: translateX(3px);
  }
`;

// eslint-disable-next-line no-unused-vars -- kept for the disabled email/password login block below
const EmailSection = styled.div`
  text-align: left;
`;

const Foot = styled.div`
  margin-top: 26px;
  font-family: 'Raleway', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .02em;
  color: #8897a8;
`;

const CrossLink = styled.div`
  margin-top: 18px;
  text-align: center;
  font-size: 14px;
`;

export default () => {
  const fetch = useFetch();

  const [isBusy, setIsBusy] = useState(false);
  const [showInvalidCodeLogin, setShowInvalidCodeLogin] = useState(false);

  const [codeChars, setCodeChars] = useState(Array(CODE_LENGTH).fill(''));
  const codeBoxRefs = useRef([]);

  const classCode = codeChars.join('');
  const isCodeComplete = (classCode.length === CODE_LENGTH);

  /* ===== EMAIL/PASSWORD LOGIN (descoped for now — uncomment to restore) =====

  const [showInvalidPassLogin, setShowInvalidPassLogin] = useState(false);

  const formSchema = {
    optionalFields: ['email', 'password'],
    initData: {
      email: '',
      password: ''
    }
  }

  const formData = useForm(formSchema)

  // Block below is necessary for password managers that auto fill inputs which never trigger components 'onChange' events
  // (the ref wraps ONLY the email/password fields so the class-code boxes don't shift the input indexes)
  const wrapperElRef = useRef()
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (wrapperElRef.current) {
        const inputs = wrapperElRef.current.getElementsByTagName('input')
        const userInput = inputs[0]
        const passwordInput = inputs[1]
        formData.setValues({ ...formData.values, email: (userInput?.value || ''), password: (passwordInput?.value || '') })
      }
    });

    if (wrapperElRef.current) {
      const inputs = wrapperElRef.current.getElementsByTagName('input')
      const userInput = inputs[0]
      if (userInput) {
        observer.observe(userInput, { attributes: true, childList: true, subtree: true });
      }
      const passwordInput = inputs[1]
      if (passwordInput) {
        observer.observe(passwordInput, { attributes: true, childList: true, subtree: true });
      }
    }

    return () => observer.disconnect()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  ===== END EMAIL/PASSWORD LOGIN ===== */

  const submitLogin = async (formValues, setShowInvalidLogin) => {
    setIsBusy(true)

    const response = await fetch(
      `auth/studentLogin`,
      {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(formValues)
      }
    )

    const jsonData = await response.json() || {};

    const { status = '' } = jsonData;

    if (status) {
      window.location.reload()
    } else {
      setShowInvalidLogin(true)
    }

    setIsBusy(false)
  }

  const onClickCodeSubmit = () => {
    if (isCodeComplete && !isBusy) {
      submitLogin({ classCode }, setShowInvalidCodeLogin)
    }
  }

  /* ===== EMAIL/PASSWORD LOGIN (descoped for now — uncomment to restore) =====

  const onClickPassSubmit = () => {
    const { email, password } = formData.values
    if (email && password && !isBusy) {
      submitLogin({ email, password }, setShowInvalidPassLogin)
    }
  }

  const onPassKeyDown = (e) => {
    if (e.key === 'Enter') {
      onClickPassSubmit();
    }
  }

  ===== END EMAIL/PASSWORD LOGIN ===== */

  const setCodeChar = (index, char) => {
    setShowInvalidCodeLogin(false)
    setCodeChars(prev => {
      const next = [...prev]
      next[index] = char
      return next
    })
  }

  const onCodeChange = (index, e) => {
    const char = (e.target.value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(-1)
    setCodeChar(index, char)
    if (char && index < CODE_LENGTH - 1) {
      codeBoxRefs.current[index + 1]?.focus()
    }
  }

  const onCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeChars[index] && index > 0) {
      e.preventDefault()
      setCodeChar(index - 1, '')
      codeBoxRefs.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      codeBoxRefs.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      codeBoxRefs.current[index + 1]?.focus()
    }
    if (e.key === 'Enter') {
      onClickCodeSubmit()
    }
  }

  const onCodePaste = (e) => {
    e.preventDefault()
    setShowInvalidCodeLogin(false)
    const chars = (e.clipboardData.getData('text') || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().split('')
    setCodeChars(Array(CODE_LENGTH).fill('').map((_, i) => chars[i] || ''))
    const next = Math.min(chars.length, CODE_LENGTH - 1)
    codeBoxRefs.current[next]?.focus()
  }

  const onTeacherLoginClicked = () => {
    const baseUrl = window.location.origin
    window.location.replace(baseUrl);
    // window.location.assign(baseUrl); // keeps history
  }

  const hintText = showInvalidCodeLogin ? 'Unable to login'
    : isCodeComplete ? 'Looks good! Press Let’s go.'
    : 'Type the code your teacher gave you'

  return (
    <Page>

      <Veil aria-hidden="true" />

      <Card>

        <Brand>
          <Logo src={prosolveLogo} alt="ProSolve" />
        </Brand>

        <Title>{'Student Login'}</Title>
        <Lead>{'Enter your class code'}</Lead>

        <CodeLabel>{'Class Code'}</CodeLabel>
        <CodeRow>
          {
            codeChars.map((char, i) => (
              <CodeBox
                key={i}
                ref={(el) => codeBoxRefs.current[i] = el}
                type='text'
                inputMode='text'
                maxLength={1}
                aria-label={`Class code character ${i + 1}`}
                autoComplete='off'
                autoCapitalize='characters'
                spellCheck={false}
                data-1p-ignore
                data-lpignore='true'
                data-bw-ignore='true'
                value={char}
                $filled={char !== ''}
                onChange={(e) => onCodeChange(i, e)}
                onKeyDown={(e) => onCodeKeyDown(i, e)}
                onPaste={onCodePaste}
              />
            ))
          }
        </CodeRow>
        <Hint $error={showInvalidCodeLogin}>{hintText}</Hint>

        <GoButton disabled={!isCodeComplete || isBusy} onClick={onClickCodeSubmit}>
          {'Let’s go'}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        </GoButton>

        {/* ===== EMAIL/PASSWORD LOGIN (descoped for now — uncomment to restore) =====

        <Divider>{'or sign in with email'}</Divider>

        <EmailSection ref={wrapperElRef}>

          <FieldWrap $invalid={false}>
            <FormField
              label='Email'
              labelStyle={fieldLabelStyle}
              fieldId='email'
              type='text'
              placeholder='you@district.org'
              formData={formData}
              onKeyDown={onPassKeyDown}
              isLoginField={true}
            />
          </FieldWrap>

          <FieldWrap $invalid={false}>
            <FormField
              label='Password'
              labelStyle={fieldLabelStyle}
              fieldId='password'
              type='password'
              placeholder='Enter your password'
              formData={formData}
              onKeyDown={onPassKeyDown}
              isLoginField={true}
              hasPasswordToggle={true}
            />
          </FieldWrap>

          <ErrorSlot>
            { (!isBusy && showInvalidPassLogin) ? 'Unable to login' : '' }
          </ErrorSlot>

          <SubmitRow>
            <SubmitButton onClick={onClickPassSubmit}>
              {'Sign In'}
            </SubmitButton>
            {
              isBusy &&
              <SpinnerWrap>
                <LoadingIcon />
              </SpinnerWrap>
            }
          </SubmitRow>

        </EmailSection>

        ===== END EMAIL/PASSWORD LOGIN ===== */}

        <Foot>{'The Human Skills Company'}</Foot>

        <CrossLink>
          <TextLink onClick={onTeacherLoginClicked} style={{ fontSize: '14px' }}>
            {'Teacher? Click here to login'}
          </TextLink>
        </CrossLink>

      </Card>
    </Page>
  )
}
